import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	updatePropertyContactHousesHouseIdContactsPropertyContactIdPutMutation,
	listPropertyContactsHousesHouseIdContactsGetQueryKey,
} from '@/client/@tanstack/react-query.gen';
import type { PropertyContactUpdate } from '@/client';
import { toaster } from '@/components/ui/toaster';
import { sharingQueryKey } from '@/hooks/sharing/useSharingList';
import { usePlanRefresh } from '@/hooks/maintenance/usePlanRefresh';

/**
 * The endpoint's refusal when a write collides with a row that already exists.
 *
 * Shared by more than one refusal — a role already served here, a Historical move it will not
 * make — so the status alone does not say which, and the caller reads it with what it sent.
 */
const CONTACT_ALREADY_IN_ROLE = 409;

/**
 * Corrects a saved contact at this property.
 *
 * Name, company, phone and email land as `override_*` on the association — the registry row keeps
 * what the documents read, which is what makes "documents say…" answerable, so read the resolved
 * value rather than `contact.*`. `tier` promotes or demotes. Partial: send only what changed, and an
 * explicit null clears a correction.
 *
 * A correction made here reaches this property alone. The same fix made before confirming becomes
 * the contact's real value everywhere, so steer a reader to the review pass where there is a choice.
 */
export const useUpdateContact = (houseId: number) => {
	const queryClient = useQueryClient();
	const refreshPlan = usePlanRefresh(houseId);

	return useMutation({
		mutationFn: async ({ propertyContactId, data }: { propertyContactId: number; data: PropertyContactUpdate }) => {
			const mutation = updatePropertyContactHousesHouseIdContactsPropertyContactIdPutMutation();
			return mutation.mutationFn!(
				{ path: { house_id: houseId, property_contact_id: propertyContactId }, body: data },
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				{} as any,
			);
		},
		onSuccess: (_updated, variables) => {
			queryClient.invalidateQueries({
				queryKey: listPropertyContactsHousesHouseIdContactsGetQueryKey({
					path: { house_id: houseId },
				}),
			});
			void queryClient.invalidateQueries({ queryKey: sharingQueryKey(houseId), refetchType: 'all' });
			// `tier` decides which of several contacts the plan card names. Notes and access state do not,
			// so do not make those edits pay for an unrelated plan read.
			if (variables.data.tier !== undefined) refreshPlan();
		},
		onError: (error: unknown, variables) => {
			const refusal = error as { status?: number; body?: { detail?: string }; message?: string } | null;
			// A contact holds one role per property, so moving it onto one it already serves here is a
			// collision rather than a failure to save. The endpoint's own wording names the category
			// rather than the contact, which is the wrong way round for a reader.
			//
			// Read with the role that was sent, not from the status alone: this endpoint also refuses
			// a Historical move with a 409, and that one is a failure to save.
			if (refusal?.status === CONTACT_ALREADY_IN_ROLE && variables.data.category_id !== undefined) {
				toaster.create({
					title: 'This contact already has that service',
					description: 'They are filed under it here already, so there is nothing to move.',
					type: 'error',
				});
				return;
			}
			toaster.create({
				title: 'Could not save this change',
				description: refusal?.body?.detail ?? refusal?.message ?? 'Please try again.',
				type: 'error',
			});
		},
	});
};
